import { apiFetch, getAuthToken } from '../lib/api';

export interface LoginResponse {
    token: string;
    user: {
        id: string;
        username: string;
        email?: string;
        role?: string;
        mustChangePassword?: boolean;
    };
}

export async function login(username: string, password: string): Promise<LoginResponse> {
    const data = await apiFetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
    });
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
}

// Sliding session: swap the current (still-valid) token for a fresh one so the
// login keeps extending while the panel is in active use. Best-effort — on any
// failure we keep the existing token (which stays valid until it actually expires).
export async function refreshToken(): Promise<void> {
    try {
        const data = await apiFetch<{ token: string }>('/api/auth/refresh', { method: 'POST' });
        if (data?.token) localStorage.setItem('authToken', data.token);
    } catch {
        // ignore: apiFetch handles a 401; otherwise we leave the current token in place
    }
}

export function logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
}

// Change the logged-in user's own password. Used both for the forced first-login
// change (admin-created / reset accounts) and voluntary changes. On success we
// clear the locally-stored mustChangePassword flag so the app stops prompting.
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    const userStr = localStorage.getItem('user');
    if (userStr) {
        try {
            const u = JSON.parse(userStr);
            u.mustChangePassword = false;
            localStorage.setItem('user', JSON.stringify(u));
        } catch {
            // ignore — worst case the app re-prompts, which is harmless
        }
    }
}

export function getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
        return JSON.parse(userStr);
    } catch {
        return null;
    }
}

// Decode a JWT payload to read its `exp` claim client-side. No signature
// verification (that's the server's job) — we only need to know if it's stale.
function decodeJwtPayload(token: string): { exp?: number } | null {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}

// True only when a NON-EXPIRED token is present. An expired (or unreadable)
// token is cleared so the app lands on the login screen on next open — instead
// of a fake "logged in" state that you'd otherwise have to manually log out of.
export function isAuthenticated(): boolean {
    const token = getAuthToken();
    if (!token || !getCurrentUser()) return false;

    const payload = decodeJwtPayload(token);
    const expMs = payload?.exp ? payload.exp * 1000 : 0;
    if (!expMs || expMs <= Date.now()) {
        logout(); // stale/unreadable — drop it so we render login, not a dead session
        return false;
    }
    return true;
}

// Re-export getAuthToken for convenience
export { getAuthToken };
